import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, ChevronRight, X, Save, Loader, AlertTriangle, Pill } from 'lucide-react';
import ApiService from './ApiService';
import { usePermissions, PERMISSIONS } from './usePermissions';
import AccessDenied from './AccessDenied';

// Modal Component - For Add/Edit Medication
const MedicationModal = ({ isOpen, onClose, onSubmit, title, submitText, formData, setFormData, error, saving, t }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">{title}</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X size={24} />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t.item.singular} Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500 focus:border-transparent"
                placeholder="e.g., Semaglutide, Metformin"
                required
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Category
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value, categoryOther: e.target.value !== 'Other' ? '' : formData.categoryOther })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500 focus:border-transparent bg-white"
              >
                <option value="">Select a category...</option>
                <option value="GLP-1">GLP-1</option>
                <option value="Longevity">Longevity</option>
                <option value="Metabolic">Metabolic</option>
                <option value="Cardiovascular">Cardiovascular</option>
                <option value="Hormone">Hormone</option>
                <option value="Vitamin">Vitamin/Supplement</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Other Category Text Box - Shows when "Other" is selected */}
            {formData.category === 'Other' && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Specify Category <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.categoryOther}
                  onChange={(e) => setFormData({ ...formData, categoryOther: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500 focus:border-transparent"
                  placeholder="Enter custom category name"
                  required
                />
              </div>
            )}

            {/* Dosage Unit */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Dosage Unit <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.dosageUnit}
                onChange={(e) => setFormData({ ...formData, dosageUnit: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500 focus:border-transparent bg-white"
                required
              >
                <option value="">Select unit...</option>
                <optgroup label="Weight">
                  <option value="mg">mg (milligrams)</option>
                  <option value="mcg">mcg (micrograms)</option>
                  <option value="g">g (grams)</option>
                </optgroup>
                <optgroup label="Volume">
                  <option value="mL">mL (milliliters)</option>
                  <option value="drops">drops</option>
                </optgroup>
                <optgroup label="Units">
                  <option value="IU">IU (International Units)</option>
                  <option value="units">units</option>
                </optgroup>
                <optgroup label="Solid Forms">
                  <option value="tablets">tablets</option>
                  <option value="capsules">capsules</option>
                </optgroup>
                <optgroup label="Other Forms">
                  <option value="puffs">puffs (inhalers)</option>
                  <option value="sprays">sprays</option>
                  <option value="patches">patches</option>
                  <option value="suppositories">suppositories</option>
                  <option value="%">% (percent)</option>
                  <option value="mg/mL">mg/mL (concentration)</option>
                </optgroup>
              </select>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500 focus:border-transparent"
                placeholder="Brief description of the medication and its purpose"
                rows="3"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Administration Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500 focus:border-transparent"
                placeholder="Special instructions, storage requirements, etc."
                rows="2"
              />
            </div>

            {/* Educational Links */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Educational Links (Optional)
              </label>
              <p className="text-sm text-gray-500 mb-3">
                Add YouTube videos, articles, or resources to help patients understand this medication
              </p>

              {/* Existing Links */}
              {formData.links && formData.links.length > 0 && (
                <div className="space-y-2 mb-3">
                  {formData.links.map((link, index) => (
                    <div key={link.id || index} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-800">{link.title}</p>
                        <p className="text-xs text-gray-500 truncate">{link.url}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const newLinks = formData.links.filter((_, i) => i !== index);
                          setFormData({ ...formData, links: newLinks });
                        }}
                        className="p-1 text-red-600 hover:bg-red-50 rounded transition"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Link Form */}
              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="Link title (e.g., How to inject)"
                  value={formData.newLinkTitle || ''}
                  onChange={(e) => setFormData({ ...formData, newLinkTitle: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-spruce-500 focus:border-transparent"
                />
                <div className="flex gap-2">
                  <input
                    type="url"
                    placeholder="URL (https://youtube.com/...)"
                    value={formData.newLinkUrl || ''}
                    onChange={(e) => setFormData({ ...formData, newLinkUrl: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-spruce-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (!formData.newLinkTitle || !formData.newLinkUrl) {
                        alert('Please enter both title and URL');
                        return;
                      }
                      try {
                        new URL(formData.newLinkUrl);
                      } catch (e) {
                        alert('Please enter a valid URL');
                        return;
                      }
                      const newLink = {
                        id: Date.now().toString(),
                        title: formData.newLinkTitle.trim(),
                        url: formData.newLinkUrl.trim(),
                        addedAt: new Date().toISOString()
                      };
                      const links = formData.links || [];
                      setFormData({
                        ...formData,
                        links: [...links, newLink],
                        newLinkTitle: '',
                        newLinkUrl: ''
                      });
                    }}
                    className="px-4 py-2 bg-spruce-600 text-white rounded-lg hover:bg-spruce-700 transition text-sm font-medium"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 bg-spruce-600 text-white py-2 px-4 rounded-lg hover:bg-spruce-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader className="animate-spin" size={18} />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    {submitText}
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const MedicationsPage = ({ config, onSelectMedication, currentUser }) => {
  const t = config?.terminology || {
    item: { singular: 'Medication', plural: 'Medications', singularLower: 'medication', pluralLower: 'medications' }
  };

  const { hasPermission } = usePermissions(currentUser);

  const [medications, setMedications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedMedication, setSelectedMedication] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [medicationToDelete, setMedicationToDelete] = useState(null);
  const [patientAssignments, setPatientAssignments] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [errorModal, setErrorModal] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    categoryOther: '',
    dosageUnit: '',
    description: '',
    notes: '',
    links: [],
    newLinkTitle: '',
    newLinkUrl: ''
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadMedications();
  }, []);

  const loadMedications = async () => {
    try {
      setLoading(true);
      const data = await ApiService.getMedications();
      setMedications(data);
    } catch (err) {
      setError('Failed to load medications');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: '',
      categoryOther: '',
      dosageUnit: '',
      description: '',
      notes: '',
      links: [],
      newLinkTitle: '',
      newLinkUrl: ''
    });
    setSelectedMedication(null);
    setError('');
  };

  const openAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  const openEditModal = (medication) => {
    setSelectedMedication(medication);
    setFormData({
      name: medication.name,
      category: medication.category || '',
      categoryOther: medication.categoryOther || '',
      dosageUnit: medication.dosageUnit,
      description: medication.description || '',
      notes: medication.notes || ''
    });
    setShowEditModal(true);
  };

  const handleAddMedication = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      // If category is "Other", use the categoryOther value
      const medicationData = {
        ...formData,
        category: formData.category === 'Other' ? formData.categoryOther : formData.category
      };
      
      const newMedication = await ApiService.createMedication(medicationData);
      setMedications([...medications, newMedication]);
      setShowAddModal(false);
      resetForm();
    } catch (err) {
      setError('Failed to create medication');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleEditMedication = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      // If category is "Other", use the categoryOther value
      const medicationData = {
        ...formData,
        category: formData.category === 'Other' ? formData.categoryOther : formData.category
      };
      
      await ApiService.updateMedication(selectedMedication.medicationId, medicationData);
      setMedications(medications.map(m => 
        m.medicationId === selectedMedication.medicationId 
          ? { ...m, ...medicationData }
          : m
      ));
      setShowEditModal(false);
      resetForm();
    } catch (err) {
      setError('Failed to update medication');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMedication = async (medication) => {
    setMedicationToDelete(medication);
    setPatientAssignments(null); // Reset
    
    // Check if medication is assigned to patients
    try {
      const response = await ApiService.checkMedicationAssignments(medication.medicationId);
      setPatientAssignments(response);
    } catch (err) {
      console.error('Error checking assignments:', err);
      setPatientAssignments(null);
    }
    
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await ApiService.deleteMedication(medicationToDelete.medicationId);
      setMedications(medications.filter(m => m.medicationId !== medicationToDelete.medicationId));
      setShowDeleteConfirm(false);
      setMedicationToDelete(null);
    } catch (err) {
      console.error('Failed to delete medication:', err);
      
      // Parse error response for patient assignment details
      let errorTitle = 'Error Deleting Medication';
      let errorMessage = 'Failed to delete medication. Please try again.';
      
      try {
        const jsonMatch = err.message.match(/\{.*\}/);
        if (jsonMatch) {
          const errorData = JSON.parse(jsonMatch[0]);
          
          if (errorData.message && errorData.message.includes('assigned to patients')) {
            errorTitle = 'Medication In Use';
            const patientCount = errorData.patientCount || 0;
            const samplePatients = errorData.samplePatients || [];
            const totalAssignments = errorData.totalAssignments || 0;
            
            errorMessage = `Cannot delete "${medicationToDelete.name}" because it is currently assigned to ${patientCount} patient${patientCount !== 1 ? 's' : ''} (${totalAssignments} total assignment${totalAssignments !== 1 ? 's' : ''}).`;
            
            if (samplePatients.length > 0) {
              errorMessage += `\n\nPatients currently using this medication:\n• ${samplePatients.join('\n• ')}`;
              if (patientCount > samplePatients.length) {
                errorMessage += `\n• ...and ${patientCount - samplePatients.length} more`;
              }
            }
            
            errorMessage += '\n\nPlease remove this medication from all patients before deleting it from the catalog.';
          } else {
            errorMessage = errorData.message || errorMessage;
          }
        } else {
          errorMessage = err.message || errorMessage;
        }
      } catch (parseErr) {
        console.error('Error parsing error message:', parseErr);
        errorMessage = err.message || errorMessage;
      }
      
      setErrorModal({ title: errorTitle, message: errorMessage });
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  const filteredMedications = medications
    .filter(medication =>
      medication.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (medication.category && medication.category.toLowerCase().includes(searchTerm.toLowerCase()))
    )
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  // Get category badge color
  const getCategoryColor = (category) => {
    const colors = {
      'GLP-1': 'bg-spruce-100 text-spruce-800',
      'Longevity': 'bg-green-100 text-green-800',
      'Metabolic': 'bg-spruce-100 text-spruce-800',
      'Cardiovascular': 'bg-red-100 text-red-800',
      'Hormone': 'bg-pink-100 text-pink-800',
      'Vitamin': 'bg-yellow-100 text-yellow-800',
      'Other': 'bg-gray-100 text-gray-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  // Check if user has permission to view medications
  if (!hasPermission(PERMISSIONS.MEDICATIONS_VIEW)) {
    return <AccessDenied resource="medications" action="view" />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">{t.item.plural}</h2>
          <p className="text-gray-500">{t.item.singular} library and inventory</p>
        </div>
        {hasPermission(PERMISSIONS.MEDICATIONS_ADD) && (
          <button 
            onClick={openAddModal}
            className="bg-spruce-600 text-white px-4 py-2 rounded-lg hover:bg-spruce-700 flex items-center gap-2"
          >
            <Plus size={20} />
            Add {t.item.singular}
          </button>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-4 relative">
          <Search className="absolute left-3 top-3 text-gray-400" size={20} />
          <input
            type="text"
            placeholder={`Search ${t.item.pluralLower}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500"
          />
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : filteredMedications.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No {t.item.pluralLower} found
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredMedications.map(medication => (
              <div key={medication.medicationId} className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition flex flex-col">
                {/* Header Section */}
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-spruce-100 rounded">
                      <Pill className="text-spruce-600" size={20} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-800">{medication.name}</h3>
                      {medication.category && (
                        <span className={`inline-block mt-2 px-2 py-1 rounded text-xs ${getCategoryColor(medication.category)}`}>
                          {medication.category}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Content Section - Compact */}
                <div className="flex-1 min-h-[80px]">
                  <div className="space-y-1 text-sm text-gray-600">
                    <p><strong>Dosage Unit:</strong> {medication.dosageUnit}</p>
                    
                    {medication.notes && (
                      <p>
                        <strong>Note:</strong> {medication.notes.length > 100 
                          ? `${medication.notes.substring(0, 100)}...` 
                          : medication.notes}
                      </p>
                    )}
                  </div>
                </div>

                {/* Audit Trail */}
                {(medication.createdBy || medication.updatedBy) && (
                  <div className="text-xs text-gray-500 mb-2 space-y-1 border-t pt-2">
                    {medication.createdBy && (
                      <div>
                        Created by: <span className="font-medium">{medication.createdBy}</span>
                        {medication.createdAt && (
                          <span className="ml-1">
                            on {new Date(medication.createdAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </span>
                        )}
                      </div>
                    )}
                    {medication.updatedBy && medication.updatedBy !== medication.createdBy && (
                      <div>
                        Updated by: <span className="font-medium">{medication.updatedBy}</span>
                        {medication.updatedAt && (
                          <span className="ml-1">
                            on {new Date(medication.updatedAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Action Buttons - Fixed at Bottom */}
                <div className="flex gap-2 pt-2 border-t border-gray-100 mt-auto">
                  {hasPermission(PERMISSIONS.MEDICATIONS_EDIT) && (
                    <button 
                      onClick={() => openEditModal(medication)}
                      className="flex-1 p-2 text-spruce-600 hover:bg-spruce-50 rounded text-sm flex items-center justify-center gap-1"
                      title="Edit medication"
                    >
                      <Edit size={16} />
                      Edit
                    </button>
                  )}
                  {hasPermission(PERMISSIONS.MEDICATIONS_DELETE) && (
                    <button 
                      onClick={() => handleDeleteMedication(medication)}
                      className="flex-1 p-2 text-red-600 hover:bg-red-50 rounded text-sm flex items-center justify-center gap-1"
                      title="Delete medication"
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Medication Modal */}
      <MedicationModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddMedication}
        title={`Add ${t.item.singular}`}
        submitText={`Add ${t.item.singular}`}
        formData={formData}
        setFormData={setFormData}
        error={error}
        saving={saving}
        t={t}
      />

      {/* Edit Medication Modal */}
      <MedicationModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSubmit={handleEditMedication}
        title={`Edit ${t.item.singular}`}
        submitText="Save Changes"
        formData={formData}
        setFormData={setFormData}
        error={error}
        saving={saving}
        t={t}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && medicationToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertTriangle className="text-red-600" size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Delete {t.item.singular}?</h3>
            </div>
            
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete <strong>{medicationToDelete.name}</strong>?
            </p>
            
            {/* Show patient assignments if any */}
            {patientAssignments && patientAssignments.patientCount > 0 ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-2 mb-2">
                  <AlertTriangle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
                  <div>
                    <p className="font-semibold text-yellow-800 mb-1">
                      Assigned to {patientAssignments.patientCount} patient{patientAssignments.patientCount !== 1 ? 's' : ''}
                    </p>
                    {patientAssignments.samplePatients && patientAssignments.samplePatients.length > 0 && (
                      <div className="text-sm text-yellow-700">
                        <p className="mb-1">Patients:</p>
                        <ul className="list-disc list-inside space-y-0.5">
                          {patientAssignments.samplePatients.map((patient, idx) => (
                            <li key={idx}>{patient}</li>
                          ))}
                          {patientAssignments.patientCount > patientAssignments.samplePatients.length && (
                            <li>...and {patientAssignments.patientCount - patientAssignments.samplePatients.length} more</li>
                          )}
                        </ul>
                      </div>
                    )}
                    <p className="text-sm text-yellow-700 mt-2">
                      You must remove this medication from all patients before deleting it.
                    </p>
                  </div>
                </div>
              </div>
            ) : patientAssignments === null ? (
              <div className="text-sm text-gray-500 mb-4">
                Checking patient assignments...
              </div>
            ) : (
              <p className="text-sm text-gray-600 mb-4">
                This medication is not currently assigned to any patients.
              </p>
            )}
            
            <p className="text-red-600 font-semibold mb-6">This action cannot be undone.</p>
            
            <div className="flex gap-3">
              <button
                onClick={confirmDelete}
                disabled={deleting || (patientAssignments && patientAssignments.patientCount > 0)}
                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <>
                    <Loader className="animate-spin" size={18} />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={18} />
                    Yes, Delete
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setMedicationToDelete(null);
                }}
                disabled={deleting}
                className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {errorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertTriangle className="text-red-600" size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-800">{errorModal.title}</h3>
            </div>
            <p className="text-gray-600 mb-6 whitespace-pre-line">
              {errorModal.message}
            </p>
            <button
              onClick={() => setErrorModal(null)}
              className="w-full bg-spruce-600 text-white py-2 px-4 rounded-lg hover:bg-spruce-700 transition"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MedicationsPage;